





















































import Vue from "vue";

import InstallationModuleDetails from "@/containers/installation";
import DialogInstallationFinished from "@/containers/installation/dialogs/InstallationFinished.vue";
import { InstallationModule, MechanicInstallation } from "@/types";
import BrowserSupport from "@/components/browser-support/BrowserSupport.vue";
import AuthorizationError from "@/components/authorization-error";
import { AxiosError } from "axios";
import visibility from "@/utilities/visibility";

type UIModule = { title: string; icon: string; type: string; module?: InstallationModule };

const modules = {
  ["PANELS-REGULAR"]: {
    title: "Buiten",
    icon: "fa-solar-panel",
    type: "PANELS-REGULAR"
  },

  ["METER-REGULAR"]: {
    title: "Binnen",
    icon: "fa-digital-tachograph",
    type: "METER-REGULAR"
  },

  ["METER_MAAS-REGULAR"]: {
    title: "Binnen",
    icon: "fa-digital-tachograph",
    type: "METER_MAAS-REGULAR"
  },

  ["TENANT"]: {
    title: "Bewoner",
    icon: "mdi-home-account",
    type: "TENANT",
    module: {
      type: "TENANT"
    }
  }
} as Record<string, UIModule>;

export default Vue.extend({
  components: {
    InstallationModule: InstallationModuleDetails,
    DialogInstallationFinished,
    BrowserSupport,
    AuthorizationError
  },

  data() {
    return {
      serviceWorkerInactive: 0,
      currentTab: -1,
      showFinishedDialog: false,
      showBrowserSupportDialog: false,
      showUnauthorisedDialog: false
    };
  },

  computed: {
    supportsServiceWorkers(): boolean {
      return "serviceWorker" in navigator;
    },

    optionalModules(): UIModule[] {
      if (!this.isMonitoringOnly && this.installation.options.tenant_signing) {
        return [modules["TENANT"]];
      }

      return [];
    },

    isMonitoringOnly(): boolean {
      return this.installation.modules.some(mod => mod.type === "METER_MAAS-REGULAR");
    },

    modules(): UIModule[] {
      if (!this.hasInstallationDetails) return [];

      return [
        ...this.installation.modules.map(mod => ({
          ...modules[mod.type],
          module: mod
        })),
        ...this.optionalModules
      ];
    },

    isEnded(): boolean {
      return !!this.installation?.ended || !!this.installation?.modules.every(mod => !!mod.ended);
    },

    canEditInMaintenance(): boolean {
      return this.$store.getters["profile/canEditInMaintenance"];
    },

    isInMaintenance(): boolean {
      // Elevated staff may reopen a handed-over installation to correct it, so they
      // are not treated as "in maintenance" and get the edit escape hatch.
      if (this.canEditInMaintenance) {
        return false;
      }

      return ["P250", "M250"].includes(this.installation?.projectProcessState || "");
    },

    isFetching(): boolean {
      return this.$store.getters["installations/isPending"];
    },

    installation(): MechanicInstallation {
      return this.$store.getters["installation/installation"](this.$router.currentRoute.params.id);
    },

    hasInstallationDetails(): boolean {
      return !!this.installation?.modules;
    }
  },

  async mounted() {
    this.showBrowserSupportDialog = !this.supportsServiceWorkers;

    this.checkServiceWorker();

    try {
      await this.update();

      this.openUnfinishedModule();

      this.$store.dispatch("materials/fetch:all");
    } catch (e) {
      this.checkForAuthorization(e);
    }
  },

  async created() {
    const uuid: string = this.$store.state.profile.uuid;

    visibility.listen(this.handleVisibilityChange);
  },

  destroyed() {
    visibility.unlisten(this.handleVisibilityChange);
  },

  methods: {
    update(): Promise<any> {
      return this.$store.dispatch("installation/fetch", this.$router.currentRoute.params.id);
    },

    handleVisibilityChange(visible: boolean) {
      if (visible) {
        this.update();
      }
    },

    checkForAuthorization(e: AxiosError) {
      this.showUnauthorisedDialog = e.response?.status === 403;
    },

    async checkServiceWorker() {
      if (!this.supportsServiceWorkers) return;

      try {
        await this.$store.dispatch("registerServiceWorker");
        await this.$store.dispatch("checkServiceWorker");
      } catch (e) {
        this.serviceWorkerInactive = 10;

        window.setInterval(() => {
          this.serviceWorkerInactive--;

          if (this.serviceWorkerInactive === 0) {
            this.reload();
          }
        }, 1000);
      }
    },

    reload() {
      window.location.reload();
    },

    openUnfinishedModule() {
      this.currentTab = this.installation.modules.findIndex(mod => !mod.ended);
    },

    checkEndedState() {
      if (this.isEnded) {
        this.showFinishedDialog = true;
      }
    }
  },

  watch: {
    currentTab() {
      this.showFinishedDialog = this.isEnded;
      this.update();
    }
  }
});
